/**
 * Functionality to handle different
 * colorations for different viewports
 */
const heroes = document.querySelectorAll(`[data-ctype="iunctim_content_hero"]`);
heroes.forEach((hero) => {
    const slider = hero.querySelector<HTMLElement>(
        `[slider][data-hero-height]`,
    );
    const updateImageHeight = () => {
        if (!slider || slider.dataset.heroHeight !== "auto-image") return;

        const viewport =
            innerWidth < 768
                ? "mobile"
                : innerWidth < 1280
                  ? "tablet"
                  : "desktop";
        const ratio = Math.max(
            0,
            ...Array.from(
                hero.querySelectorAll<HTMLElement>(
                    `[data-hero-image-ratio-${viewport}]`,
                ),
            )
                .map((slide) =>
                    Number.parseFloat(
                        slide.dataset[
                            `heroImageRatio${viewport[0].toUpperCase()}${viewport.slice(1)}`
                        ] ?? "",
                    ),
                )
                .filter(Number.isFinite),
        );

        if (ratio > 0) {
            slider.style.setProperty(
                "--hero-image-height",
                `${Math.ceil(slider.clientWidth * ratio)}px`,
            );
        } else {
            slider.style.removeProperty("--hero-image-height");
        }

        const swiper = (
            slider.querySelector<HTMLElement>(`.swiper`) as
                | (HTMLElement & {
                      swiper?: { update(): void; emit(event: string): void };
                  })
                | null
        )?.swiper;
        swiper?.update();
        swiper?.emit("resize");
    };

    updateImageHeight();
    window.addEventListener("resize", updateImageHeight);

    const updateSplitContentHeight = () => {
        const splitSlides = Array.from(
            hero.querySelectorAll<HTMLElement>(`[data-hero-split]`),
        );
        if (!splitSlides.length) return;

        const viewportIndex = innerWidth < 768 ? 0 : innerWidth < 1280 ? 1 : 2;
        const contentPosition = (slide: HTMLElement) =>
            slide.dataset.contentPositionVertical
                ?.split(",")
                [viewportIndex]?.trim() === "bottom"
                ? "bottom"
                : "top";

        splitSlides.forEach((slide) => {
            slide.style.removeProperty("--hero-split-content-height");
        });

        if (!matchMedia("(max-aspect-ratio: 1/1)").matches) {
            return;
        }

        const orderedSlides = [...splitSlides].sort(
            (first, second) =>
                Number(first.dataset.heroSplitIndex) -
                Number(second.dataset.heroSplitIndex),
        );
        const series: HTMLElement[][] = [];
        const swiper = (
            slider?.querySelector<HTMLElement>(`.swiper`) as
                | (HTMLElement & {
                      swiper?: {
                          update(): void;
                          emit(event: string): void;
                          params?: { loop?: boolean };
                      };
                  })
                | null
        )?.swiper;

        orderedSlides.forEach((slide) => {
            const position = contentPosition(slide);
            const currentSeries = series.at(-1);
            const currentPosition = currentSeries
                ? contentPosition(currentSeries[0])
                : undefined;

            if (!currentSeries || position !== currentPosition) {
                series.push([slide]);
                return;
            }

            currentSeries.push(slide);
        });

        const firstSeries = series[0];
        const lastSeries = series.at(-1);
        if (
            swiper?.params?.loop &&
            series.length > 1 &&
            firstSeries &&
            lastSeries &&
            contentPosition(firstSeries[0]) === contentPosition(lastSeries[0])
        ) {
            firstSeries.unshift(...lastSeries);
            series.pop();
        }

        series.forEach((slides) => {
            const contentHeight = Math.max(
                0,
                ...slides.map(
                    (slide) =>
                        slide.querySelector<HTMLElement>(
                            `[data-hero-split-content]`,
                        )?.scrollHeight ?? 0,
                ),
            );

            if (!contentHeight) return;

            slides.forEach((slide) => {
                slide.style.setProperty(
                    "--hero-split-content-height",
                    `${Math.ceil(contentHeight)}px`,
                );
            });
        });

        swiper?.update();
        swiper?.emit("resize");
    };

    updateSplitContentHeight();
    window.addEventListener("resize", updateSplitContentHeight);

    const updateSplitNavigationClearance = () => {
        const splitContents = Array.from(
            hero.querySelectorAll<HTMLElement>(`[data-hero-split-content]`),
        );
        splitContents.forEach((content) => {
            content.style.removeProperty("padding-bottom");
        });

        updateSplitContentHeight();

        if (slider?.dataset.navigationPosition !== "internal") return;

        const controls = slider.querySelector<HTMLElement>(`.controls-wrapper`);
        if (!controls || controls.getBoundingClientRect().height === 0) return;

        const ensureClearance = (remainingAttempts: number) => {
            let hasAdjustedPadding = false;
            const navigationTop = controls.getBoundingClientRect().top;

            splitContents.forEach((content) => {
                const contentInner =
                    content.querySelector<HTMLElement>(`:scope > div`);
                if (!contentInner) return;

                const styles = getComputedStyle(content);
                const minimumGap = Number.parseFloat(styles.paddingTop);
                const gap =
                    navigationTop - contentInner.getBoundingClientRect().bottom;
                if (gap >= minimumGap) return;

                const paddingBottom = Number.parseFloat(styles.paddingBottom);
                content.style.paddingBottom = `${Math.ceil(
                    paddingBottom + minimumGap - gap,
                )}px`;
                hasAdjustedPadding = true;
            });

            if (hasAdjustedPadding && remainingAttempts > 0) {
                requestAnimationFrame(() =>
                    ensureClearance(remainingAttempts - 1),
                );
                return;
            }

            if (hasAdjustedPadding) updateSplitContentHeight();
        };

        requestAnimationFrame(() => ensureClearance(2));
    };

    updateSplitNavigationClearance();
    window.addEventListener("resize", updateSplitNavigationClearance);

    const slides = hero.querySelectorAll<HTMLElement>(
        `[data-coloration-default]`,
    );
    slides.forEach((slide) => {
        let remove = "";
        remove += slide.dataset.colorationDefault;
        remove += slide.dataset.colorationMobile;
        remove += slide.dataset.colorationTablet;
        remove += slide.dataset.colorationDesktop;

        const removeClasses = remove
            .split(" ")
            .map((c) => c.trim())
            .filter((c) => c.length);

        const applyLayerColoration = () => {
            let add = slide.dataset.colorationDefault;
            if (innerWidth < 640) {
                add = slide.dataset.colorationMobile ?? add;
            } else if (innerWidth < 1024) {
                add = slide.dataset.colorationTablet ?? add;
            } else {
                add = slide.dataset.colorationDesktop ?? add;
            }

            removeClasses.forEach((c) => {
                slide.classList.remove(c);
            });

            add?.split(" ")
                .map((c) => c.trim())
                .filter((c) => c.length)
                .forEach((c) => {
                    slide.classList.add(c);
                });
        };

        applyLayerColoration();
        window.addEventListener("resize", applyLayerColoration);
    });
});
